import { jsx } from "react/jsx-runtime";
import { ContentType } from "../../../enums/contentStack.js";
import { ContentStack } from "../../../services/content/business.client.js";
import { findOneBusinessEntry, DEFAULT_FIELD_EXCEPTIONS } from "../../../services/content/business.lib.js";
import { AnnouncementBanner } from "../../Chrome/Header/AnnouncementBanner/index.js";
import { AdBuilder } from "../../Marketing/AdBuilder/index.js";
import { AdSet } from "../../Marketing/AdSet/index.js";
import { FeatureHighlightCardV2 } from "../../Marketing/FeatureHighlightCardv2/index.js";
import { RichTextEntry } from "../../Media/RichTextEntry/index.js";
import { LinkFlyout } from "../../Navigation/LinkFlyout/index.js";
import { LinkList } from "../../Navigation/LinkList/index.js";
import { SegmentBasedEntryAJOClient } from "./SegmentBasedEntryAJOClient.js";
import { SegmentBasedEntryClient } from "./SegmentBasedEntryClient.js";
import { findMatchingRuleByAttributes } from "./utils.js";
const AJO_SEGMENTS_VALUE = "ajo_segments";
const DIGITAL_SEGMENTS_CLIENT_VALUE = "digital_segments_client";
const renderComponentForReference = async ({
  referenceToUse,
  locale,
  enableLivePreview,
  linkListProps,
  adSetCostcoProps,
  adBuilderSx,
  entry
}) => {
  if (!referenceToUse) {
    return null;
  }
  const contentType = referenceToUse == null ? void 0 : referenceToUse._content_type_uid;
  const analyticsTitlePrefix = entry == null ? void 0 : entry.title;
  switch (contentType) {
    case ContentType.AdBuilder:
      return /* @__PURE__ */ jsx(
        AdBuilder,
        {
          entryId: referenceToUse.uid,
          locale,
          enableLivePreview,
          sx: adBuilderSx,
          analyticsTitlePrefix
        }
      );
    case ContentType.AdSetCostco:
      return /* @__PURE__ */ jsx(
        AdSet,
        {
          entryId: referenceToUse.uid,
          contentType: referenceToUse._content_type_uid,
          locale,
          enableLivePreview,
          analyticsTitlePrefix,
          ...adSetCostcoProps
        }
      );
    case ContentType.LinkListSimple:
      return /* @__PURE__ */ jsx(
        LinkList,
        {
          entryId: referenceToUse.uid,
          locale,
          enableLivePreview,
          analyticsTitlePrefix,
          ...linkListProps
        }
      );
    case ContentType.LinkFlyout:
      return /* @__PURE__ */ jsx(
        LinkFlyout,
        {
          entryId: referenceToUse.uid,
          locale,
          enableLivePreview,
          analyticsTitlePrefix,
          ...linkListProps
        }
      );
    case ContentType.FeatureHighlightCardV2:
      return /* @__PURE__ */ jsx(
        FeatureHighlightCardV2,
        {
          entryId: referenceToUse.uid,
          locale,
          enableLivePreview,
          analyticsTitlePrefix
        }
      );
    case ContentType.CustomRichText:
      return /* @__PURE__ */ jsx(
        RichTextEntry,
        {
          entryId: referenceToUse.uid,
          locale,
          enableLivePreview
        }
      );
    case ContentType.AnnouncementBanner:
      return /* @__PURE__ */ jsx(
        AnnouncementBanner,
        {
          entryId: referenceToUse.uid,
          locale,
          enableLivePreview,
          analyticsTitlePrefix
        }
      );
    default:
      return null;
  }
};
async function SegmentBasedEntry({
  entryId,
  locale,
  enableLivePreview,
  linkListProps,
  adSetCostcoProps,
  adBuilderSx
}) {
  var _a, _b, _c, _d, _e;
  const entry = await findOneBusinessEntry(
    ContentType.SegmentBasedEntry,
    entryId,
    locale,
    DEFAULT_FIELD_EXCEPTIONS,
    enableLivePreview
  );
  if (!entry) {
    return null;
  }
  if ((entry == null ? void 0 : entry.segment_source) === AJO_SEGMENTS_VALUE) {
    return /* @__PURE__ */ jsx(
      SegmentBasedEntryAJOClient,
      {
        entry,
        locale,
        enableLivePreview,
        linkListProps,
        adSetCostcoProps,
        adBuilderSx
      }
    );
  } else if ((entry == null ? void 0 : entry.segment_source) === DIGITAL_SEGMENTS_CLIENT_VALUE) {
    const allReferencedEntries = [
      (_a = entry == null ? void 0 : entry.base_content_reference) == null ? void 0 : _a[0],
      ...((_b = entry == null ? void 0 : entry.target_rules_block) == null ? void 0 : _b.map(
        ({ inclusion_rule, exclusion_rule }) => {
          var _a2, _b2;
          if (inclusion_rule) {
            return (_a2 = inclusion_rule == null ? void 0 : inclusion_rule.segmented_content_reference) == null ? void 0 : _a2[0];
          } else if (exclusion_rule) {
            return (_b2 = exclusion_rule == null ? void 0 : exclusion_rule.segmented_content_reference) == null ? void 0 : _b2[0];
          }
        }
      )) || []
    ].filter((ref) => !!ref);
    const ReferencedEntriesComponentList = await Promise.all(
      allReferencedEntries.map(async (referencedEntry) => {
        const component = await renderComponentForReference({
          referenceToUse: referencedEntry,
          locale,
          enableLivePreview,
          linkListProps,
          adSetCostcoProps,
          entry
        });
        return {
          referencedEntry,
          component
        };
      })
    );
    const ReferenceEntriesComponentMap = ReferencedEntriesComponentList.reduce(
      (componentMap, referenceEntryComponent) => {
        componentMap[referenceEntryComponent.referencedEntry.uid] = referenceEntryComponent.component;
        return componentMap;
      },
      {}
    );
    return /* @__PURE__ */ jsx(
      SegmentBasedEntryClient,
      {
        ReferenceEntriesComponentMap,
        defaultContentType: (_d = (_c = entry == null ? void 0 : entry.base_content_reference) == null ? void 0 : _c[0]) == null ? void 0 : _d._content_type_uid,
        entry,
        locale,
        linkListProps,
        adSetCostcoProps,
        adBuilderSx
      }
    );
  }
  const attributes = ContentStack.env["attributes"] || {};
  const defaultReference = (_e = entry == null ? void 0 : entry.base_content_reference) == null ? void 0 : _e[0];
  const targetRules = (entry == null ? void 0 : entry.target_rules_block) || [];
  const matchingReference = findMatchingRuleByAttributes(
    targetRules,
    attributes
  );
  const referenceToUse = matchingReference || defaultReference;
  if (!referenceToUse) {
    return null;
  }
  return renderComponentForReference({
    referenceToUse,
    locale,
    enableLivePreview,
    linkListProps,
    adSetCostcoProps,
    adBuilderSx,
    entry
  });
}
export {
  SegmentBasedEntry
};
